import React from 'react';
import { SvgIcon, SvgIconProps } from '@mui/material';

/**
 * Sidebar navigation icons, redrawn from the Figma design as 24x24 line icons.
 * They are stroked with currentColor so they take the sidebar's text colour
 * and the MUI `fontSize` / `sx` props like any MUI icon.
 */
const LineIcon: React.FC<SvgIconProps> = ({ children, sx, ...props }) => (
  <SvgIcon
    viewBox="0 0 24 24"
    sx={{
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 1.5,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      ...sx,
    }}
    {...props}
  >
    {children}
  </SvgIcon>
);

/** Two toggle switches (Control Panel). */
export const ControlPanelIcon: React.FC<SvgIconProps> = (p) => (
  <LineIcon {...p}>
    <rect x="3" y="4" width="18" height="7" rx="3.5" />
    <circle cx="16.5" cy="7.5" r="1.8" />
    <rect x="3" y="13" width="18" height="7" rx="3.5" />
    <circle cx="7.5" cy="16.5" r="1.8" />
  </LineIcon>
);

/** Connected nodes (Hardware). */
export const HardwareNodesIcon: React.FC<SvgIconProps> = (p) => (
  <LineIcon {...p}>
    <circle cx="12" cy="12" r="2.4" />
    <circle cx="5" cy="6.5" r="1.9" />
    <circle cx="19" cy="6" r="1.9" />
    <circle cx="5.5" cy="18" r="1.9" />
    <circle cx="18.5" cy="18" r="1.9" />
    <path d="M6.5 7.6 10.2 10.6M17.6 7.2 13.8 10.6M7 16.8l3.3-3.1M17.1 16.7l-3.4-3.1" />
  </LineIcon>
);

/** City buildings (Projects). */
export const ProjectsBuildingsIcon: React.FC<SvgIconProps> = (p) => (
  <LineIcon {...p}>
    <path d="M2.5 21h19" />
    <path d="M8 21V4.5A1.5 1.5 0 0 1 9.5 3h5A1.5 1.5 0 0 1 16 4.5V21" />
    <path d="M3.5 21v-9.5A1.5 1.5 0 0 1 5 10h3" />
    <path d="M20.5 21V8.5A1.5 1.5 0 0 0 19 7h-3" />
    <path d="M10.5 6.5h3M10.5 9.5h3M10.5 12.5h3M10.5 15.5h3M5.5 13.5h.01M5.5 16.5h.01M18.5 10.5h.01M18.5 13.5h.01M18.5 16.5h.01" />
    <path d="M11 21v-3h2v3" />
  </LineIcon>
);

/** Factory with chimney (Batching Plants). */
export const BatchingPlantIcon: React.FC<SvgIconProps> = (p) => (
  <LineIcon {...p}>
    <path d="M3 21V11l5 3v-3l5 3v-3l5 3V21H3Z" />
    <path d="M4.5 11V5.5h3V13" />
    <path d="M9 6.5c1-.8 1-1.7 0-2.5M11.5 6.5c1-.8 1-1.7 0-2.5" />
    <path d="M6.5 17.5h2M11 17.5h2M15.5 17.5h2" />
    <path d="M2 21h20" />
  </LineIcon>
);

/** Concrete mixer truck (Mix / Recipes). */
export const MixerTruckIcon: React.FC<SvgIconProps> = (p) => (
  <LineIcon {...p}>
    <path d="M2 17v-5.5a1 1 0 0 1 1-1h3.6L9.3 14H10" />
    <path d="M3.6 14h5.7" />
    <path d="M10 17l2-8.2a2.2 2.2 0 0 1 2.1-1.6h4.4a2.4 2.4 0 0 1 2.3 3.1L19.3 17" />
    <path d="M11.4 12h9.4" />
    <path d="M2 17h20" />
    <circle cx="6" cy="18.8" r="1.8" />
    <circle cx="17" cy="18.8" r="1.8" />
  </LineIcon>
);

/** Two people (Users). */
export const UsersGroupIcon: React.FC<SvgIconProps> = (p) => (
  <LineIcon {...p}>
    <circle cx="9.5" cy="8" r="3.2" />
    <path d="M3.5 20a6 6 0 0 1 12 0" />
    <path d="M15.5 5a3.2 3.2 0 0 1 0 6" />
    <path d="M17.2 14.3a6 6 0 0 1 3.8 5.7" />
  </LineIcon>
);

/** Bar chart in a frame (Limits & Targets). */
export const LimitsChartIcon: React.FC<SvgIconProps> = (p) => (
  <LineIcon {...p}>
    <rect x="3" y="4.5" width="18" height="15" rx="2" />
    <path d="M8 15.5v-4M12 15.5v-7M16 15.5v-5.5" />
  </LineIcon>
);

/** Cog (Settings). */
export const SettingsCogIcon: React.FC<SvgIconProps> = (p) => (
  <LineIcon {...p}>
    <path d="M19.57 10.11 22.07 10.4v3.2l-2.5.29-.88 2.13 1.56 1.98-2.25 2.25-1.98-1.56-2.13.88-.29 2.5h-3.2l-.29-2.5-2.13-.88-1.98 1.56-2.25-2.25 1.56-1.98-.88-2.13-2.5-.29v-3.2l2.5-.29.88-2.13-1.56-1.98L6 3.75l1.98 1.56 2.13-.88.29-2.5h3.2l.29 2.5 2.13.88L18 3.75l2.25 2.25-1.56 1.98.88 2.13Z" />
    <circle cx="12" cy="12" r="3.2" />
  </LineIcon>
);

/** Graduation cap (Training). */
export const TrainingCapIcon: React.FC<SvgIconProps> = (p) => (
  <LineIcon {...p}>
    <path d="M2 9.5 12 5l10 4.5-10 4.5L2 9.5Z" />
    <path d="M6.5 11.5V15c0 1.7 2.5 3 5.5 3s5.5-1.3 5.5-3v-3.5" />
    <path d="M22 9.5v5" />
  </LineIcon>
);

/** Slider controls (Administration). */
export const AdminSlidersIcon: React.FC<SvgIconProps> = (p) => (
  <LineIcon {...p}>
    <path d="M3 7h9.6M17.4 7H21" />
    <circle cx="15" cy="7" r="1.9" />
    <path d="M3 12h2.6M10.4 12H21" />
    <circle cx="8" cy="12" r="1.9" />
    <path d="M3 17h11.1M18.9 17H21" />
    <circle cx="16.5" cy="17" r="1.9" />
  </LineIcon>
);

/** Document with a contact badge (Clients). */
export const ClientsCardIcon: React.FC<SvgIconProps> = (p) => (
  <LineIcon {...p}>
    <path d="M13.5 18H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5.5" />
    <path d="M7 9.5h10M7 13h6" />
    <circle cx="17.5" cy="15.8" r="2.2" />
    <path d="M13.5 22a4 4 0 0 1 8 0" />
  </LineIcon>
);

/** Document with a small building on it (Project list rows / summary card). */
export const ProjectDocumentIcon: React.FC<SvgIconProps> = (p) => (
  <LineIcon {...p}>
    <path d="M14 2.5H6.5A1.5 1.5 0 0 0 5 4v16a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 20V7.5L14 2.5z" />
    <path d="M14 2.5v5h5" />
    <path d="M7.5 17.5v-4.6l2.6-2.3 2.6 2.3v4.6" />
    <path d="M11.3 11.6V9.9h1.2v2.8" />
    <path d="M9.4 17.5v-2h1.4v2" />
    <path d="M14.5 13.5h2M14.5 15.5h2M14.5 17.5h2" />
  </LineIcon>
);
